import { useTranslation } from '../i18n';
import { useNbTheme } from '../hooks/useNbTheme';
import ButtonNB from './ui-nb/ButtonNB';
import { Link } from 'react-router-dom';

export default function PricingNB() {
  const { t, language } = useTranslation();
  const { theme, colors } = useNbTheme();
  const isDark = theme === 'dark';

  const baseCard = {
    border: `3px solid ${colors.textPrimary}`,
    borderRadius: '2px',
    padding: 'clamp(1.25rem, 3vw, 2rem)',
    background: colors.bgCard,
  };

  const highlightedCard = {
    ...baseCard,
    border: `3px solid ${colors.accentPrimary || '#FFD600'}`,
    background: isDark ? 'rgba(255,214,0,0.06)' : '#FFFDE7',
  };

  const tiers = [
    {
      key: 'free',
      style: baseCard,
      highlight: false,
      priceColor: colors.textPrimary,
    },
    {
      key: 'pro',
      style: highlightedCard,
      highlight: true,
      priceColor: colors.textPrimary,
    },
  ];

  return (
    <section
      className="py-12 md:py-20"
      style={{ background: colors.bgSection || '#F0F0F0' }}
    >
      <div className="max-w-[1000px] mx-auto px-4 md:px-8">
        {/* Section header */}
        <div className="text-left mb-8 md:mb-12">
          <h2
            className="font-nb font-bold mb-3"
            style={{
              color: colors.textPrimary,
              fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
              lineHeight: 1.2,
            }}
          >
            {t('pricing.title')}
          </h2>
          <p
            className="font-nb"
            style={{
              color: colors.textSecondary,
              fontSize: 'clamp(1rem, 1.5vw, 1.25rem)',
              lineHeight: 1.6,
            }}
          >
            {t('pricing.subtitle')}
          </p>
        </div>

        {/* Tier cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {tiers.map((tier) => (
            <div key={tier.key} style={tier.style}>
              {/* Highlight badge (or spacer for alignment) */}
              {tier.highlight ? (
                <div className="mb-3">
                  <span
                    className="font-nb font-bold text-xs px-2 py-1"
                    style={{
                      background: colors.accentPrimary || '#FFD600',
                      color: isDark ? '#0D0D0D' : '#1C1C1C',
                      border: `2px solid ${colors.textPrimary}`,
                    }}
                  >
                    {t('pricing.pro.name').toUpperCase()} — {t('pricing.cta')}
                  </span>
                </div>
              ) : (
                <div className="mb-3" style={{ height: '32px' }} />
              )}

              {/* Name */}
              <h3
                className="font-nb font-bold text-xl mb-2"
                style={{ color: colors.textPrimary }}
              >
                {t(`pricing.${tier.key}.name`)}
              </h3>

              {/* Price */}
              <div className="flex items-baseline gap-1 mb-2">
                <span
                  className="font-nb-mono font-bold"
                  style={{
                    color: tier.priceColor,
                    fontSize: 'clamp(2rem, 4vw, 2.75rem)',
                    lineHeight: 1,
                  }}
                >
                  {t(`pricing.${tier.key}.price`)}
                </span>
                <span
                  className="font-nb text-sm"
                  style={{ color: colors.textSecondary }}
                >
                  {t(`pricing.${tier.key}.period`)}
                </span>
              </div>

              {/* Description */}
              <p
                className="font-nb text-sm mb-5"
                style={{ color: colors.textSecondary }}
              >
                {t(`pricing.${tier.key}.description`)}
              </p>

              {/* CTA */}
              <div className="mb-5">
                <Link to={`/${language}/register`} className="block">
                  <ButtonNB
                    variant={tier.highlight ? 'primary' : 'secondary'}
                    size="lg"
                    className="w-full"
                  >
                    {t('pricing.cta')}
                  </ButtonNB>
                </Link>
              </div>

              {/* Feature list */}
              <ul className="space-y-2">
                {Array.from({ length: tier.key === 'pro' ? 6 : 4 }, (_, i) => {
                  const featureKey = `pricing.${tier.key}.feature${i + 1}`;
                  const text = t(featureKey);
                  if (text === featureKey) return null; // Skip missing keys
                  return (
                    <li
                      key={i}
                      className="flex items-start gap-2 font-nb text-sm"
                      style={{ color: colors.textSecondary }}
                    >
                      <span
                        className="flex-shrink-0 mt-0.5 font-bold"
                        style={{ color: colors.accentFunctional || '#0D9488' }}
                      >
                        ✓
                      </span>
                      {text}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        {/* Trial note */}
        <p
          className="font-nb text-sm text-center mt-6"
          style={{ color: colors.textMuted }}
        >
          {t('pricing.trial')}
        </p>
      </div>
    </section>
  );
}
